import React, {
  useCallback,
  useEffect,
  useState
} from 'react';

import {
  Link
} from 'react-router-dom';

import {
  Building,
  CheckCircle,
  AlertCircle,
  Database,
  Power,
  RefreshCw,
  Server,
  Upload,
  Users,
  WalletCards,
  Wifi
} from 'lucide-react';

import {
  useSettings
} from '../contexts/SettingsContext';

import WhatsAppQRCode
  from '../components/WhatsAppQRCode';

import PushNotificationsCard
  from '../components/PushNotificationsCard';

const API = (
  process.env.REACT_APP_API_URL ||
  '/api'
).replace(/\/$/, '');

export default function SettingsSafe() {
  const {
    companyName,
    setCompanyName,
    companyLogo,
    setCompanyLogo,
    handleLogoUpload,
    darkMode
  } = useSettings();

  const [health, setHealth] = useState(null);
  const [integrations, setIntegrations] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [logoError, setLogoError] = useState('');
  const [restarting, setRestarting] =
    useState('');

  const bg = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const card = darkMode ? 'bg-gray-800' : 'bg-white';
  const border = darkMode
    ? 'border-gray-700'
    : 'border-gray-200';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const muted = darkMode
    ? 'text-gray-400'
    : 'text-gray-600';

  const carregarStatus = useCallback(async () => {
    setLoading(true);

    try {
        const response = await fetch(
          `${API}/settings/status`,
          {
            credentials: 'include'
          }
        );

        const data =
          await response.json();

        if (
          !response.ok ||
          data.success === false
        ) {
          throw new Error(
            data.error ||
            'Falha ao consultar status'
          );
        }

        setHealth({
          status: 'ok',
          services:
            data.services || {},
          automations:
            data.automations || {},
          checked_at:
            data.checked_at || null
        });

        setIntegrations(
          data.integrations || {}
        );
    } catch (error) {
      console.error(
        'Erro ao carregar configurações:',
        error
      );

      setHealth({
        status: 'error',
        services: {}
      });

      setIntegrations({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarStatus();
  }, [carregarStatus]);

  const enviarReinicio = async (
    tipo,
    endpoint
  ) => {
    if (!window.confirm(
      `Confirma a reinicialização de ${tipo}?`
    )) {
      return;
    }

    setRestarting(tipo);

    try {
      const response = await fetch(
        `${API}${endpoint}`, {
        credentials: 'include',method: 'POST' }
      );

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(
          data.message ||
          data.error ||
          'Falha na reinicialização'
        );
      }

      window.alert(
        `Reinicialização de ${tipo} iniciada.`
      );
    } catch (error) {
      window.alert(
        `Erro ao reiniciar ${tipo}: ${error.message}`
      );
    } finally {
      setRestarting('');
    }
  };

  const StatusItem = ({
    nome,
    ativo,
    desativado = false,
    detalhe,
    icon: Icon
  }) => {
    const cores = ativo
      ? {
          fundo: 'bg-green-100 text-green-700',
          icone: 'text-green-600',
          status: 'Operacional'
        }
      : desativado
        ? {
            fundo: 'bg-yellow-100 text-yellow-700',
            icone: 'text-yellow-600',
            status: 'Desativado'
          }
        : {
            fundo: 'bg-red-100 text-red-700',
            icone: 'text-red-600',
            status: 'Não configurado'
          };

    return (
      <div className={
        `${card} ${border} border rounded-xl p-4 h-full`
      }>
        <div className="flex items-start justify-between gap-4 h-full">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`${cores.fundo} p-2 rounded-lg`}>
              <Icon size={20} />
            </div>

            <div className="flex-1 min-w-0">
              <div className={`${text} font-semibold`}>
                {nome}
              </div>

              <div className={`${muted} text-xs mt-1 leading-5`}>
                {detalhe}
              </div>

              <div className={`${cores.icone} text-xs mt-1 font-medium`}>
                {cores.status}
              </div>
            </div>
          </div>

          {ativo ? (
            <CheckCircle
              size={21}
              className={cores.icone}
            />
          ) : (
            <AlertCircle
              size={21}
              className={cores.icone}
            />
          )}
        </div>
      </div>
    );
  };

  const enviarLogo = async event => {
    const file = event.target.files?.[0];

    if (!file) return;

    setLogoError('');

    try {
      await handleLogoUpload(file);
    } catch (error) {
      setLogoError(
        error.message ||
        'Não foi possível enviar a logo.'
      );
    }
  };

  const removerLogo = () => {
    setCompanyLogo('');
    setLogoError('');
  };

  const servicos = health?.services || {};

  const integracoes = integrations || {};

  if (loading) {
    return (
      <div className={`${bg} min-h-screen p-6`}>
        <div className="flex items-center justify-center py-20">
          <RefreshCw
            size={28}
            className="animate-spin text-blue-600"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`${bg} min-h-screen p-6 space-y-6`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className={`${text} text-3xl font-bold`}>
            Configurações
          </h1>

          <p className={`${muted} mt-1`}>
            Saúde do sistema, integrações e identidade visual
          </p>
        </div>

        <button
          onClick={carregarStatus}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
        >
          <RefreshCw size={17} />
          Atualizar status
        </button>
      </div>

      <section>
        <h2 className={`${text} text-lg font-semibold mb-3`}>
          Saúde do Sistema
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
          <StatusItem
            nome="API Backend"
            ativo={servicos.api === 'ok'}
            detalhe="Porta 3001"
            icon={Server}
          />

          <StatusItem
            nome="PostgreSQL"
            ativo={servicos.postgres === 'ok'}
            detalhe="Banco principal"
            icon={Database}
          />

          <StatusItem
            nome="Armazenamento V2"
            ativo={servicos.state_store === 'ok'}
            detalhe="Contexto persistente no PostgreSQL"
            icon={Database}
          />

            <StatusItem
              nome="Agendadores"
              ativo={servicos.scheduler === 'ok'}
              desativado={servicos.scheduler === 'disabled'}
              detalhe="Follow-up e remarketing V2"
              icon={RefreshCw}
            />

        </div>
      </section>

      <section>
        <h2 className={`${text} text-lg font-semibold mb-3`}>
          Integrações
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
          <StatusItem
            nome="Mercado Pago"
            ativo={integracoes.mercado_pago === 'configured'}
              desativado={integracoes.mercado_pago === 'disabled'}
            detalhe="PIX e confirmação de pagamentos"
            icon={WalletCards}
          />

          <StatusItem
            nome="Assinafy"
            ativo={
              Boolean(integracoes.assinafy)
            }
            detalhe="Contratos e assinatura digital"
            icon={CheckCircle}
          />

          <StatusItem
            nome="ChatGPT"
            ativo={integracoes.openai === 'configured'}
              desativado={integracoes.openai === 'disabled'}
            detalhe="Dúvidas fora do roteiro e classificação de intenção"
            icon={Wifi}
          />

          <StatusItem
            nome="Gemini"
            ativo={integracoes.gemini === 'configured'}
              desativado={integracoes.gemini === 'disabled'}
            detalhe="Inteligência artificial de contingência"
            icon={Wifi}
          />


          <StatusItem
            nome="Atendimento Vitor"
            ativo={integracoes.consultor_vitor === 'configured'}
              desativado={integracoes.consultor_vitor === 'disabled'}
            detalhe="Fluxo Limpa Nome V2"
            icon={CheckCircle}
          />
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${card} ${border} border rounded-xl p-6`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="bg-blue-100 text-blue-700 p-2 rounded-lg">
              <Building size={22} />
            </div>

            <div>
              <h2 className={`${text} text-lg font-semibold`}>
                Identidade da Empresa
              </h2>

              <p className={`${muted} text-sm`}>
                Nome e logo exibidos no painel
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className={`${text} block text-sm font-medium mb-1`}>
                Nome da empresa
              </label>

              <input
                type="text"
                value={companyName}
                onChange={event =>
                  setCompanyName(event.target.value)
                }
                className={
                  `${
                    darkMode
                      ? 'bg-gray-900 border-gray-700 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } w-full border rounded-lg px-4 py-2.5`
                }
              />
            </div>

            <div>
              <label className={`${text} block text-sm font-medium mb-1`}>
                Logo da empresa
              </label>

              <label className={
                `${
                  darkMode
                    ? 'bg-gray-900 border-gray-700 text-gray-300'
                    : 'bg-gray-50 border-gray-300 text-gray-700'
                } flex items-center justify-center gap-2 border border-dashed rounded-lg px-4 py-5 cursor-pointer`
              }>
                <Upload size={19} />
                Enviar nova logo

                <input
                  type="file"
                  accept="image/*"
                  onChange={enviarLogo}
                  className="hidden"
                />
              </label>

              {logoError && (
                <div className="text-red-600 text-sm mt-2">
                  {logoError}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={`${card} ${border} border rounded-xl p-6`}>
          <h2 className={`${text} text-lg font-semibold mb-4`}>
            WhatsApp Business
          </h2>

          <WhatsAppQRCode />
        </div>
      </section>

      {companyLogo && (
        <section className={`${card} ${border} border rounded-xl p-6`}>
          <div className="flex flex-wrap items-center gap-5">
            <img
              src={companyLogo}
              alt="Logo da empresa"
              className="h-20 w-20 rounded-xl object-cover border"
            />

            <div className="flex-1">
              <div className={`${text} font-semibold`}>
                Logo atual
              </div>

              <div className={`${muted} text-sm mt-1`}>
                Esta imagem é exibida no painel.
              </div>
            </div>

            <button
              onClick={removerLogo}
              className="px-4 py-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
            >
              Remover logo
            </button>
          </div>
        </section>
      )}

              <PushNotificationsCard />

<section className={`${card} ${border} border rounded-xl p-6`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 text-purple-700 p-2 rounded-lg">
              <Users size={22} />
            </div>

            <div>
              <h2 className={`${text} text-lg font-semibold`}>
                Usuários do Painel
              </h2>

              <p className={`${muted} text-sm`}>
                Cadastre, edite, desative ou exclua acessos da equipe.
              </p>
            </div>
          </div>

          <Link
            to="/settings/users"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium"
          >
            <Users size={17} />
            Gerenciar usuários
          </Link>
        </div>
      </section>

      <section className={`${card} ${border} border rounded-xl p-6`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-red-100 text-red-700 p-2 rounded-lg">
            <Power size={22} />
          </div>

          <div>
            <h2 className={`${text} text-lg font-semibold`}>
              Administração do Sistema
            </h2>

            <p className={`${muted} text-sm`}>
              Use somente quando houver necessidade técnica.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() =>
              enviarReinicio(
                'Backend',
                '/admin/restart-backend'
              )
            }
            disabled={Boolean(restarting)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
          >
            <Server size={17} />
            {restarting === 'Backend'
              ? 'Reiniciando...'
              : 'Reiniciar Backend'}
          </button>

          <button
            onClick={() =>
              enviarReinicio(
                'WhatsApp',
                '/admin/restart-whatsapp'
              )
            }
            disabled={Boolean(restarting)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          >
            <Wifi size={17} />
            {restarting === 'WhatsApp'
              ? 'Reiniciando...'
              : 'Reiniciar WhatsApp'}
          </button>

          
        </div>
      </section>

      <div className={`${muted} text-xs text-center pb-4`}>
        Ambiente de produção · Credenciais protegidas no servidor
      </div>
    </div>
  );
}
